import { useEffect, useMemo, useState } from "react"

import { App, Flex, Spin, Typography } from "antd"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useLocation, useNavigate } from "react-router"
import { useSearchParams } from "react-router-dom"

import ButtonComponent from "../../../Components/Button"
import withPermission from "../../../hoc/withPermission"
import { useExternalDemoMode } from "../../../hooks/useExternalDemoMode"
import Icon from "../../../Icon"
import { extendedAccessRoles } from "../../../Utils/permission"
import { userSetProp } from "../../User/redux/user.actions"
import { getEnrollmentsWithoutDescriptors } from "../../User/redux/user.thunks"
import { enrollmentBtnTextMap, getEnrollmentBtnIcon } from "../utils/Enrollment.util"
import DescriptorListFilters from "./components/DescriptorListFilters/DescriptorListFilters"
import DescriptorsList from "./components/DescriptorsList/DescriptorsList"
import EnrollmentCard from "./components/EnrollmentCard/EnrollmentCard"
import EnrollmentCodesListItem from "./components/EnrollmentCodesListItem/EnrollmentCodesListItem"
import EnrollmentTabs from "./components/EnrollmentTabs/EnrollmentTabs"
import useAvailableDescriptorStatuses from "./hooks/useAvailableDescriptorStatuses"
import useFetchDescriptorsList from "./hooks/useFetchDescriptorsList"
import useFetchVisaCodesList from "./hooks/useFetchVisaCodesList"

import "./styles.less"

const { Text } = Typography

const PAGE_SIZE = 20
const VISA_CODES_PAGE_SIZE = 50

const enrollmentOptions = [
  { value: "Ethoca", label: "Ethoca", icon: "mastercard" },
  { value: "RDR", label: "RDR", icon: "visa" },
  { value: "CDRN", label: "CDRN", icon: "visa" }
]

const PermissionButtonExtended = withPermission(ButtonComponent, extendedAccessRoles)

const Enrollments = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { notification } = App.useApp()
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const [params, setParams] = useSearchParams()
  const tab = params.get("tab") || "Ethoca"
  const { userEnrollments, userEnrollmentsFetching } = useSelector((state) => state.user)
  const { userInfo, isAdmin } = useSelector((state) => state.auth)

  const isExternalDemoMode = useExternalDemoMode()
  const business_account_id = userInfo?.current_business_account_id

  const [isFiltersUpdated, setIsFiltersUpdated] = useState(false)
  const [viewMode, setViewMode] = useState("descriptor")
  const [filters, setFilters] = useState({
    statuses: [],
    search: ""
  })

  const enrollment = useMemo(() => {
    return userEnrollments?.find(
      (enrollment) => enrollment?.name?.toLowerCase() === tab?.toLowerCase()
    )
  }, [userEnrollments, tab])

  const { statuses: availableDescriptorStatuses, loading: availableDescriptorStatusesLoading } =
    useAvailableDescriptorStatuses(enrollment?.id)

  const businessAccount = useSelector((state) => state.user.businessAccount)

  const isDemo = userInfo?.is_demo && userInfo?.ba_role?.name !== "Administrator"
  const isEmptyBaId = !userInfo.current_business_account_id
  const isDemoModeBlocked = isDemo || isEmptyBaId
  const isAllStepsCompleted = businessAccount && businessAccount?.status !== "incomplete"

  const setTab = (value) => {
    params.set("tab", value)
    setParams(params)
  }

  const setManageEnrollmentStep = (step) => {
    dispatch(userSetProp({ key: "nextManageEnrollmentStep", value: step }))
  }

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters)
    setIsFiltersUpdated(true)
  }

  const handleTabChange = (value) => {
    setTab(value)
    setIsFiltersUpdated(false)
    setViewMode("descriptor")
    setFilters({
      statuses: [],
      search: ""
    })
  }

  const handleNavigate = (enrollment) => {
    if (!enrollment) return
    if (isAdmin || userInfo?.is_active) {
      setManageEnrollmentStep(2)
      navigate(`${pathname}/${enrollment.id}/alerts`)
    }
    if (!isAdmin && !userInfo?.is_active) {
      notification.error({
        message: t("Enrollments are not editable"),
        description: t(
          "Enrollments are not editable in inactive business accounts. Please check Billing settings."
        ),
        closeIcon: <Text>{t("Dismiss")}</Text>,
        style: { minWidth: "495px" }
      })
    }
  }

  useEffect(() => {
    dispatch(getEnrollmentsWithoutDescriptors(business_account_id))
  }, [business_account_id])

  // Reset the filters for every enrollment, seeding them with the statuses that
  // enrollment actually has - otherwise the previous enrollment's statuses leak into
  // the descriptors request and it comes back empty.
  useEffect(() => {
    if (!enrollment?.id || availableDescriptorStatusesLoading) return
    setFilters({ statuses: availableDescriptorStatuses, search: "" })
  }, [enrollment?.id, availableDescriptorStatuses, availableDescriptorStatusesLoading])

  const { descriptors, fetchMore, hasMore, loading } = useFetchDescriptorsList({
    enrollmentId: enrollment?.id,
    filters,
    statusesReady: !availableDescriptorStatusesLoading,
    PAGE_SIZE,
    userEnrollmentsFetching
  })

  const isEmptyDescriptorsList = useMemo(() => {
    return descriptors?.length === 0
  }, [descriptors])

  // Fetched as soon as the RDR tab is active (not gated behind the "Codes" toggle) -
  // the backend may return no codes at all, and we need that answer before the user
  // switches the toggle so we know whether to disable it.
  const { codes, loading: codesLoading } = useFetchVisaCodesList({
    businessAccountId: business_account_id,
    search: filters.search,
    PAGE_SIZE: VISA_CODES_PAGE_SIZE,
    enabled: tab === "RDR"
  })

  const isCodesDisabled = tab === "RDR" && !codesLoading && codes.length === 0

  useEffect(() => {
    if (isCodesDisabled && viewMode === "codes") setViewMode("descriptor")
  }, [isCodesDisabled, viewMode])

  const btnIcon = getEnrollmentBtnIcon(enrollment?.["connection_status"])

  if (!enrollment) {
    return null
  }

  return (
    <div className="enrollments-page" data-tour="enrollment">
      <EnrollmentTabs
        options={enrollmentOptions}
        value={tab}
        onChange={handleTabChange}
        className="enrollment-tabs"
      />
      <Flex gap={24} align="flex-start" className="enrollment-content">
        <EnrollmentCard enrollment={enrollment} status={enrollment?.["connection_status"]}>
          <PermissionButtonExtended
            size="large"
            type={"primary"}
            loading={false}
            icon={btnIcon && <Icon type={btnIcon} style={{ fontSize: 20 }} />}
            className={"flex center"}
            onClick={() => handleNavigate(enrollment)}
            role={userInfo?.ba_role?.id}
            style={{ width: "100%", height: "36px" }}
            disabled={isExternalDemoMode || isDemoModeBlocked || !isAllStepsCompleted}
          >
            {enrollmentBtnTextMap[enrollment?.name?.toLowerCase()]?.[
              enrollment?.["connection_status"]
            ] || "Add more Descriptors"}
          </PermissionButtonExtended>
        </EnrollmentCard>
        <Flex style={{ flexGrow: 1, overflow: "hidden" }} gap={24} vertical>
          {(!isEmptyDescriptorsList || isFiltersUpdated) && (
            <DescriptorListFilters
              filters={filters}
              onFiltersChange={handleFiltersChange}
              statusOptions={availableDescriptorStatuses}
              statusOptionsLoading={availableDescriptorStatusesLoading}
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              showViewToggle={tab === "RDR"}
              isCodesDisabled={isCodesDisabled}
            />
          )}
          {viewMode === "descriptor" ? (
            <DescriptorsList
              enrollment={enrollment}
              descriptors={descriptors}
              loading={loading}
              isFiltered={isFiltersUpdated}
              onLoadMore={fetchMore}
              hasMore={hasMore}
            />
          ) : (
            <div className="enrollment-codes-list">
              {codesLoading ? (
                <Flex justify="center" align="center" style={{ minHeight: 160 }}>
                  <Spin size="large" />
                </Flex>
              ) : codes.length ? (
                codes.map((item) => <EnrollmentCodesListItem key={item.id} item={item} />)
              ) : (
                <Text type="secondary">No codes found.</Text>
              )}
            </div>
          )}
        </Flex>
      </Flex>
    </div>
  )
}

export default Enrollments
